import { metrics } from '@geo-admin/shared/contract/api/index.ts';
import { useParams } from '@tanstack/react-router';
import { chipsFound, MentionSnippet } from '../components/MentionSnippet.tsx';
import { Badge, Card, CardBody, CardHeader } from '../components/ui/card.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi } from '../lib/api.ts';
import { openWithSession } from '../lib/call.ts';
import { dateTime, OUTCOME_LABEL } from '../lib/format.ts';
import { impersonation } from '../lib/impersonation.ts';
import { useMe } from '../lib/me.ts';
import { PageTitle } from '../components/PageTitle.tsx';

export function SampleDetailPage() {
  // ★ 이제 **운영자 영역(/admin) 전용**이다 — 고객 영역에서 라우트를 뺐다 (260921-client-report-chart-only).
  //   strict:false 는 그대로 둔다: 라우트 id 를 고정하지 않는 편이 다시 공용이 돼도 안전하다
  const { id } = useParams({ strict: false }) as { id: string };
  const s = useApi(metrics.sample, { params: { id } });
  const me = useMe();
  if (s.isLoading) return <Loading />;
  if (s.error) return <ErrorBox error={s.error} />;
  const v = s.data!;
  // 우리 사이트 인용 — siteUrlPrefix 는 admin 에게만 오므로 비-admin 이면 빈 목록이다
  const ours = v.citations.filter((c) => c.siteUrlPrefix);
  const ourNames = ours.flatMap((c) => (c.attribution ? [c.attribution] : []));
  const found = v.answerText ? chipsFound(v.answerText, ourNames) : new Set<string>();
  return (
    <>
      <PageTitle
        title={v.queryText}
        description={`${dateTime(v.createdAt)} · ${v.outcome ? OUTCOME_LABEL[v.outcome] : '진행 중'}${v.errorKind ? ` (${v.errorKind})` : ''} · ${v.eligible ? '적격' : '지표에서 제외'}`}
      />
      <div className="flex flex-wrap gap-2 text-sm">
        <Badge tone={v.mention.inAnswer ? 'green' : 'slate'}>본문 언급 {v.mention.inAnswer ? '있음' : '없음'}</Badge>
        <Badge tone={v.mention.inPlaceCard ? 'green' : 'slate'}>
          장소 카드 {v.mention.inPlaceCard ? '있음' : '없음'}
        </Badge>
        {v.operator ? (
          <Badge tone={ours.length ? 'green' : 'slate'}>우리 사이트 인용 {ours.length ? '있음' : '없음'}</Badge>
        ) : null}
        <span className="text-slate-500">목표 브랜드명: {v.brandNames.join(' · ')}</span>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader
            title="답변 원문"
            description={`강조 = 목표 브랜드명으로 판정된 구절입니다. 짧은 이름은 다른 이름 안에서 잘못 잡힐 수 있어 여기서 눈으로 확인합니다${ours.length ? ' · 초록 = 우리 사이트 인용 칩과 그 칩이 붙은 줄입니다' : ''}`}
          />
          <CardBody>
            {v.answerText ? (
              <MentionSnippet text={v.answerText} snippets={v.mention.snippets} ourChips={ourNames} />
            ) : (
              <p className="text-sm text-slate-500">답변이 없습니다</p>
            )}
          </CardBody>
        </Card>
        <div className="space-y-4">
          <Card>
            <CardHeader title={`인용 ${v.citations.length}`} />
            <CardBody className="space-y-2">
              {v.citations.map((c) => (
                <div key={c.position} className="text-sm">
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-400">{c.position}.</span>
                    <a href={c.url} target="_blank" rel="noreferrer noopener" className="font-medium hover:underline">
                      {c.host}
                    </a>
                    {c.siteUrlPrefix ? <Badge tone="green">우리 사이트</Badge> : null}
                  </div>
                  {c.siteUrlPrefix && !(c.attribution && found.has(c.attribution)) ? (
                    // 묶음 칩(`모모두닥+1`)의 둘째 이후 출처는 원문에 이름이 없다 — 강조가 없다고 인용이 없는 게 아니다
                    <div className="text-xs text-amber-700">
                      본문에서 칩을 찾지 못했습니다 — 다른 출처의 +N 묶음 안에 있을 수 있습니다
                    </div>
                  ) : null}
                  {c.title ? <div className="text-xs text-slate-500">{c.title}</div> : null}
                </div>
              ))}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title={`장소 카드 ${v.entities.length}`} />
            <CardBody className="space-y-1">
              {v.entities.map((e) => (
                <div key={e.position} className="flex items-center gap-1.5 text-sm">
                  <span className="text-slate-400">{e.position}.</span>
                  <span>{e.name}</span>
                  {e.matched ? <Badge tone="green">목표</Badge> : null}
                </div>
              ))}
            </CardBody>
          </Card>
          {v.rawFiles.length > 0 ? (
            <Card>
              <CardHeader title="원본 파일" />
              <CardBody className="flex flex-wrap gap-2 text-sm">
                {v.rawFiles.map((f) => {
                  const url = `/api/samples/${v.id}/raw/${f}`;
                  const cls = 'rounded border border-slate-200 px-2 py-1 hover:bg-slate-50';
                  // 링크는 헤더를 못 싣는다 — 운영자는 쿼리로 영역을 알리고, 대리 탭은 헤더를 실어 blob 으로 연다
                  if (impersonation())
                    return (
                      <button
                        key={f}
                        type="button"
                        className={cls}
                        onClick={() => void openWithSession(url).then((ok) => ok || alert('파일을 열지 못했습니다'))}
                      >
                        {f}
                      </button>
                    );
                  return (
                    <a
                      key={f}
                      href={me.data?.role === 'admin' ? `${url}?realm=admin` : url}
                      target="_blank"
                      rel="noreferrer"
                      className={cls}
                    >
                      {f}
                    </a>
                  );
                })}
              </CardBody>
            </Card>
          ) : null}
          {v.operator ? (
            <Card>
              <CardHeader title="측정 조건 (운영자)" />
              <CardBody className="space-y-1 text-xs text-slate-600">
                <p>
                  워커 {v.operator.workerName} · 세대 {v.operator.generation}
                </p>
                <p>Accept-Language {v.operator.acceptLanguage ?? '—'}</p>
                <p>
                  egress {v.operator.egress?.ip ?? '—'} {v.operator.egress?.asnOrg ?? ''}{' '}
                  {v.operator.egress?.country ?? ''}
                </p>
                <p>
                  {v.operator.probeVersion} · {v.operator.chromeVersion ?? '—'}
                </p>
                <details>
                  <summary className="cursor-pointer">진단</summary>
                  <pre className="mt-1 max-h-64 overflow-auto rounded bg-slate-50 p-2">
                    {JSON.stringify(v.operator.diagnostics, null, 2)}
                  </pre>
                </details>
              </CardBody>
            </Card>
          ) : null}
        </div>
      </div>
    </>
  );
}
